import apiService from './api';
import { Motorista, Veiculo, Viagem, ApiResponse } from '../types';

export interface PortalMeResponse extends ApiResponse<Motorista> {
  estatisticas?: any;
}

/** Portal do Motorista — o motorista logado consome apenas os próprios dados. */
/** Lancamento de abastecimento feito pelo proprio motorista. */
export interface AbastecimentoDoMotorista {
  veiculo_id: number;
  data_abastecimento: string;
  quilometragem: number;
  litros: number;
  preco_litro: number;
  tipo_combustivel: string;
  posto: string;
  cidade: string;
  estado: string;
  comprovantes: string[];
  observacoes?: string;
}

/**
 * Resposta do envio do comprovante: só o caminho gravado. O arquivo não é lido
 * (sem OCR); o gestor confere o lançamento por ele.
 */
export interface ComprovanteEnviado {
  path: string;
  url: string;
}

/** Lancamento de despesa feito pelo proprio motorista. */
export interface DespesaDoMotorista {
  veiculo_id: number;
  categoria: string;
  descricao: string;
  // Digitado: o comprovante da despesa não é lido, só anexado.
  valor: number;
  data_despesa: string;
  comprovantes: string[];
  observacoes?: string;
}

/** Um item do checklist, como o servidor o descreve. */
export interface ItemDoChecklist {
  chave: string;
  rotulo: string;
}

/** A resposta de um item: OK ou nao, e o que ha de errado quando nao. */
export interface RespostaDoChecklist {
  chave: string;
  ok: boolean;
  observacao: string | null;
}

export interface ChecklistPreenchido {
  itens: RespostaDoChecklist[];
  fotos: string[];
  observacoes?: string | null;
  /** O veiculo conferido. Se for outro que o planejado, a viagem passa a usa-lo. */
  veiculo_id?: number;
}

/**
 * A viagem como ela chega na tela do dia.
 *
 * `checklist_feito` vem do servidor junto da viagem para a tela saber qual
 * botao mostrar sem uma segunda chamada por viagem — no 4G do motorista, cada
 * ida e volta pesa.
 */
export interface ViagemDoDia extends Viagem {
  checklist_feito: boolean;
}

export const portalService = {
  getMe: async (): Promise<PortalMeResponse> => {
    return apiService.get<PortalMeResponse>('/portal/me');
  },

  getViagens: async (): Promise<ApiResponse<Viagem[]>> => {
    return apiService.get<ApiResponse<Viagem[]>>('/portal/viagens');
  },

  getVeiculos: async (): Promise<ApiResponse<Veiculo[]>> => {
    return apiService.get<ApiResponse<Veiculo[]>>('/portal/veiculos');
  },

  /**
   * Envia o comprovante e devolve o caminho gravado.
   *
   * O motorista nao alcanca o upload da gestao (que tambem permite apagar e
   * listar anexos da empresa inteira), entao o portal tem o proprio endpoint,
   * restrito a foto ou PDF e a pasta de comprovantes da empresa dele.
   */
  enviarComprovante: async (
    arquivo: File,
    aoProgredir?: (p: number) => void,
  ): Promise<ApiResponse<ComprovanteEnviado>> => {
    return apiService.upload<ApiResponse<ComprovanteEnviado>>(
      '/portal/comprovantes',
      arquivo,
      aoProgredir,
    );
  },

  criarAbastecimento: async (dados: AbastecimentoDoMotorista): Promise<ApiResponse<any>> => {
    return apiService.post<ApiResponse<any>>('/portal/abastecimentos', dados);
  },

  criarDespesa: async (dados: DespesaDoMotorista): Promise<ApiResponse<any>> => {
    return apiService.post<ApiResponse<any>>('/portal/despesas', dados);
  },

  /**
   * A quilometragem da viagem, informada por quem esta olhando para o painel.
   *
   * Sao dois momentos separados de proposito: o km de saida abre a viagem e o
   * de chegada fecha. Um POST cada, como o resto do portal — o motorista lanca,
   * e quem corrige e a gestao.
   */
  /**
   * As viagens de hoje, uma de cada vez.
   *
   * Endpoint proprio, e nao a lista geral: com viagem fixa o motorista tem uma
   * viagem cadastrada para cada dia do mes, e a regra de qual mostrar e do
   * servidor — a tela nao tem como decidir sozinha sem baixar o mes inteiro.
   */
  getViagensDeHoje: async (): Promise<ApiResponse<ViagemDoDia[]>> => {
    return apiService.get<ApiResponse<ViagemDoDia[]>>('/portal/viagens/hoje');
  },

  /**
   * As categorias de despesa da empresa.
   *
   * Vem do servidor porque cada empresa tem a sua: uma lista fixa na tela
   * mostraria ao motorista categorias que a empresa dele apagou, e esconderia
   * as que ela criou.
   */
  getCategoriasDeDespesa: async (): Promise<ApiResponse<Array<{ id: number; nome: string; slug: string }>>> => {
    return apiService.get<ApiResponse<Array<{ id: number; nome: string; slug: string }>>>(
      '/portal/categorias-despesa',
    );
  },

  /** O catalogo de itens do checklist, que mora no servidor. */
  getItensDoChecklist: async (): Promise<ApiResponse<ItemDoChecklist[]>> => {
    return apiService.get<ApiResponse<ItemDoChecklist[]>>('/portal/checklist/itens');
  },

  enviarFotoDoChecklist: async (
    arquivo: File,
    aoProgredir?: (p: number) => void,
  ): Promise<ApiResponse<{ path: string; url: string }>> => {
    return apiService.upload<ApiResponse<{ path: string; url: string }>>(
      '/portal/checklist/fotos',
      arquivo,
      aoProgredir,
    );
  },

  salvarChecklist: async (
    viagemId: number,
    dados: ChecklistPreenchido,
  ): Promise<ApiResponse<any>> => {
    return apiService.post<ApiResponse<any>>(`/portal/viagens/${viagemId}/checklist`, dados);
  },

  iniciarViagem: async (id: number, dados: { km_saida: number }): Promise<ApiResponse<any>> => {
    return apiService.post<ApiResponse<any>>(`/portal/viagens/${id}/iniciar`, dados);
  },

  finalizarViagem: async (
    id: number,
    dados: { km_chegada: number; km_saida?: number },
  ): Promise<ApiResponse<any>> => {
    return apiService.post<ApiResponse<any>>(`/portal/viagens/${id}/finalizar`, dados);
  },
};
